import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMessageTemplates, createMessageTemplate, updateMessageTemplate, toggleMessageTemplate,
  getSmsGateways, getActiveSmsGateway, createSmsGateway, updateSmsGateway, toggleSmsGateway,
  getSmsCharges, getActiveSmsCharge, createSmsCharge, toggleSmsCharge,
  getProviders, setDefaultProvider,
} from "@/services/notification.service";

/* --------------------------------- Message templates ---------------------------------- */

export const useMessageTemplates = (params = {}, options = {}) =>
  useQuery({
    queryKey: ["message-templates", params.page, params.size],
    queryFn: () => getMessageTemplates(params),
    ...options,
  });

const useTemplateMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["message-templates"] }),
  });
};

export const useCreateMessageTemplate = () => useTemplateMutation(createMessageTemplate);
export const useUpdateMessageTemplate = () => useTemplateMutation(updateMessageTemplate);
export const useToggleMessageTemplate = () => useTemplateMutation(toggleMessageTemplate);

/* ------------------------------------ SMS gateways ------------------------------------ */

export const useSmsGateways = () =>
  useQuery({ queryKey: ["sms-gateways"], queryFn: getSmsGateways });

export const useActiveSmsGateway = () =>
  useQuery({ queryKey: ["active-sms-gateway"], queryFn: getActiveSmsGateway });

// Only one gateway is active at a time, so activating one deactivates another.
// Both the list and the active-gateway query are invalidated — a single-row
// optimistic update would leave the previously active row wrongly shown as on.
const useGatewayMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-gateways"] });
      queryClient.invalidateQueries({ queryKey: ["active-sms-gateway"] });
    },
  });
};

export const useCreateSmsGateway = () => useGatewayMutation(createSmsGateway);
export const useUpdateSmsGateway = () => useGatewayMutation(updateSmsGateway);
export const useToggleSmsGateway = () => useGatewayMutation(toggleSmsGateway);

/* ------------------------------------- SMS charges ------------------------------------ */

export const useSmsCharges = () =>
  useQuery({ queryKey: ["sms-charges"], queryFn: getSmsCharges });

export const useActiveSmsCharge = () =>
  useQuery({ queryKey: ["active-sms-charge"], queryFn: getActiveSmsCharge });

const useChargeMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-charges"] });
      queryClient.invalidateQueries({ queryKey: ["active-sms-charge"] });
    },
  });
};

export const useCreateSmsCharge = () => useChargeMutation(createSmsCharge);
export const useToggleSmsCharge = () => useChargeMutation(toggleSmsCharge);

/* -------------------------------------- Providers ------------------------------------- */

export const useProviders = () =>
  useQuery({ queryKey: ["providers"], queryFn: getProviders });

export const useSetDefaultProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setDefaultProvider,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["providers"] }),
  });
};